import type { BookingSummaryFields } from "@/app/booking-calendar/components/BookingDetailsPanel"
import type { BookingReviewDetail } from "@/app/booking-calendar/api/reviewApi"
import { slotTypeLabelForSlug } from "@/app/booking-calendar/lib/slotTypes"
import {
  SITE_TIME_ZONE,
  longDateLabel,
  minutesToLabel,
  zonedDateAndMinutes,
} from "@/app/booking-calendar/lib/siteTime"

/** "intake" → "Public", "private" → "Private", "off-slot" → "Managed"; an unknown slug is shown as it is. */
export function eventTypeLabel(slug: string | null | undefined): string | null {
  return slotTypeLabelForSlug(slug)
}

/**
 * "Tuesday, Mar 3, 2026 · 9:00 AM": the appointment's date and start time only, in the site's zone
 * (D2) rather than the browser's. The Booking Review queue shows this (AB#796); the end is not needed.
 */
export function appointmentStartLabel(
  start: string | null | undefined,
  timeZone: string = SITE_TIME_ZONE,
): string | null {
  if (!start) return null
  const from = zonedDateAndMinutes(start, timeZone)
  return `${longDateLabel(from.date)}, ${from.date.slice(0, 4)} · ${minutesToLabel(from.minutes)}`
}

/**
 * "Tuesday, Mar 3, 2026 · 9:00 AM – 9:30 AM", in the site's zone (D2) rather than the browser's.
 * The end is left off until the sweep has read it back from Cal; an end on a different site-local
 * day carries its own date.
 */
export function appointmentWindowLabel(
  start: string | null | undefined,
  end: string | null | undefined,
  timeZone: string = SITE_TIME_ZONE,
): string | null {
  if (!start) return null
  const startLabel = appointmentStartLabel(start, timeZone)!
  if (!end) return startLabel

  const from = zonedDateAndMinutes(start, timeZone)
  const to = zonedDateAndMinutes(end, timeZone)
  const endLabel = to.date === from.date
    ? minutesToLabel(to.minutes)
    : `${longDateLabel(to.date)}, ${to.date.slice(0, 4)} · ${minutesToLabel(to.minutes)}`
  return `${startLabel} – ${endLabel}`
}

/** A value the booker typed, or null when they left it blank. */
function entered(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/**
 * What the review modal's booking summary shows, from the review detail alone. The phone number,
 * address and account number are null when the booker left them blank, which leaves their rows out.
 */
export function reviewBookingSummary(detail: BookingReviewDetail, timeZone: string = SITE_TIME_ZONE): BookingSummaryFields {
  const item = detail.item
  const end = detail.slotEnd ?? item.slotEnd ?? null

  return {
    referenceCode: item.referenceCode,
    confirmationCode: item.confirmationCode ?? null,
    customerName: item.attendeeName || null,
    hostName: detail.hostName ?? item.hostName ?? null,
    eventType: eventTypeLabel(detail.eventTypeSlug ?? item.eventTypeSlug),
    when: appointmentWindowLabel(item.slotStart, end, timeZone),
    siteName: item.siteName,
    status: detail.status ?? item.status,
    phoneNumber: entered(item.phoneNumber),
    address: entered(item.address),
    energyAccountNumber: entered(item.energyAccountNumber),
  }
}
